/**
 * The saved-view control on the project register: the menu that lists them and
 * the dialog that makes one.
 *
 * A saved view IS a `ListView` - the same object the address bar is read into -
 * so applying one is writing its filter set back into the address bar and
 * letting `readView` do the rest. Nothing here parses a stored value: that is
 * `applyQuery` in `listView.ts`, which is also the file that decides a stored
 * key nobody recognises widens the register instead of breaking it. What lives
 * here is the list, who may do what to it, and how the operator is told which
 * one they are looking at.
 *
 * ── THE MARK ON THE ACTIVE ROW IS A CANONICAL COMPARISON ───────────────────
 *
 * Both sides are put through the SAME round trip before they are compared:
 * `viewQuery(applyQuery(stored))` against `viewQuery(onScreen)`. That is not
 * ceremony. `CrmSavedView.query` is jsonb, whose key order is not the order it
 * went in with, so a raw `JSON.stringify` comparison reports "no view active"
 * while the operator is looking straight at one - and the next thing they do is
 * save a duplicate of the view they already have. Running the stored side
 * through the parser as well earns a second thing: a view still carrying a key
 * this build no longer has compares equal to the live filter set it actually
 * produces, so a stale view is marked active when it IS active rather than never
 * again.
 *
 * ── WHAT IS OFFERED ON SOMEBODY ELSE'S VIEW, AND WHY IT IS ONLY "APPLY" ────
 *
 * The list is "mine plus shared", but the writes are "mine only" - the API
 * refuses a PATCH or a DELETE on a colleague's view, and it is right to: being
 * allowed to SEE a shared filter is not being allowed to rename it out from
 * under the person who made it. So Delete is drawn on a row only when `mine` is
 * true. An action that is drawn and then refused is worse than an action that
 * was never there; it teaches operators that the app is broken and that clicking
 * things is how you find out.
 *
 * Rename IS offered now, on the operator's own rows only, and the reason it
 * stopped being "delete and save it again" is what that workaround costs on a
 * SHARED view: the row is the only copy, so fixing a typo took it out of every
 * colleague's sidebar and put a different one back, and anybody who had it open
 * lost the view rather than the typo. A rename touches nothing but the label.
 *
 * Reorder is still a door the API opens and this menu does not. It needs a drag
 * handle in a panel whose every row is a single click that does one thing, and
 * nobody has enough saved views to want it yet.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Bookmark, Check, ChevronDown, Loader2, Pencil, Plus, Trash2, Users } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { ApiError } from "../../../lib/api";
import {
  canonQuery,
  createSavedView,
  deleteSavedView,
  getSavedViews,
  updateSavedView,
  type SavedView,
  type SavedViewQuery,
} from "../../../lib/savedViews";
import { Pop, useClosePops } from "../../shell/Pop";
import { Btn, Modal, useToast } from "../../ui";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { Field } from "../_shared/formBits";
import { applyQuery, viewQuery, type ListView } from "./listView";

/** Which register these belong to. A constant rather than a prop: this file is
 *  the projects toolbar's control, and a `section` that could be passed in is a
 *  `section` that can be passed in wrongly - which is the one mistake that fails
 *  silently, with a saved view that applies nothing on a screen that cannot read
 *  it. The task register gets its own thin caller, not a parameter here. */
const SECTION = "projects" as const;

/** The server's order, kept locally so a row added by a save lands where the
 *  next reload will put it rather than at the end of whatever this component
 *  happens to hold. Ties break on `id` for the reason every register sort does:
 *  `sortOrder` is 0 on every untouched row, and without a tiebreaker the list
 *  re-orders itself between renders with nobody having touched anything. */
function bySort(a: SavedView, b: SavedView): number {
  if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

export function SavedViews({
  filters,
  onApply,
}: {
  /** Everything the register is being asked for right now: what gets saved, and
   *  what the active mark is compared against. */
  filters: ListView;
  /**
   * A whole question, not a patch.
   *
   * The toolbar's other controls emit one changed field and let the screen add
   * the page reset; this one replaces the lot, including the page and the search
   * term, because that is what "show me this view" means. The screen passes its
   * patch handler straight in, and because the object carries `page` explicitly
   * the reset logic there leaves it alone rather than adding a second one.
   */
  onApply: (next: ListView) => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();
  const [rows, setRows] = useState<SavedView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<SavedView | null>(null);
  const [renaming, setRenaming] = useState<SavedView | null>(null);

  useEffect(() => {
    let alive = true;
    getSavedViews(SECTION)
      .then((res) => {
        if (alive) setRows(res.rows.slice().sort(bySort));
      })
      // Swallowed on purpose. A list of saved views that could not be fetched is
      // a menu with nothing in it, which is exactly what the empty state below
      // already says in words - and a toast over a register that is drawing
      // correctly underneath would be an alarm about a sidebar.
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  /** What is on screen, in the shape a saved view stores. Built once per
   *  question rather than per row: `filters` is memoised off the query string,
   *  so this recomputes exactly when the register changes. */
  const current = useMemo(() => canonQuery(viewQuery(filters)), [filters]);

  const active = useMemo(
    () => rows.find((row) => canonQuery(viewQuery(applyQuery(row.query))) === current) ?? null,
    [rows, current],
  );

  const apply = useCallback(
    (row: SavedView) => {
      close();
      onApply(applyQuery(row.query));
    },
    [close, onApply],
  );

  const mine = rows.filter((row) => row.mine);
  // Everything the list returned that is not the operator's own is somebody
  // else's shared view - the endpoint answers with "mine OR shared" and nothing
  // else, so this needs no second condition and must not grow one.
  const theirs = rows.filter((row) => !row.mine);

  function row(v: SavedView) {
    const on = active?.id === v.id;
    return (
      // A wrapper rather than a button inside a button: a menu row that carries
      // its own delete is two controls, and nesting them is invalid markup that
      // browsers repair by moving the inner one out of the row entirely.
      <div key={v.id} className="flex items-center">
        <button
          type="button"
          className={cn("crm-mi min-w-0 flex-1", on && "is-on")}
          onClick={() => apply(v)}
        >
          <span className="crm-trunc flex-1">{v.name}</span>
          {/* Only on the operator's OWN views. On a colleague's row every entry
              in the list is shared, so the icon would say nothing; on mine it is
              the only place the choice made in the save dialog is still
              visible. */}
          {v.mine && v.shared ? <Users className="size-4" aria-label={t("projects.sharedView")} /> : null}
          {/* Kept in the layout when off, or every row shifts sideways as the
              tick appears and disappears under the pointer. */}
          <Check className={cn("size-4", !on && "opacity-0")} />
        </button>
        {/* Both writes the API allows, and only on rows it allows them on. They
            sit outside the apply button rather than inside it for the reason
            stated above the wrapper: a control inside a control is markup the
            browser repairs by moving one of them out of the row. */}
        {v.mine ? (
          <>
            <button
              type="button"
              className="crm-cb crm-cb-sm flex-none"
              title={t("projects.renameView")}
              aria-label={t("projects.renameView")}
              onClick={() => {
                close();
                setRenaming(v);
              }}
            >
              <Pencil className="size-3.5" />
            </button>
            <button
              type="button"
              className="crm-cb crm-cb-sm mr-2 flex-none"
              title={t("projects.deleteView")}
              aria-label={t("projects.deleteView")}
              onClick={() => {
                close();
                setRemoving(v);
              }}
            >
              <Trash2 className="size-3.5" />
            </button>
          </>
        ) : null}
      </div>
    );
  }

  /**
   * Delete one, and say so when it does not happen.
   *
   * The row is dropped on a 404 as well as on a success, and reading the status
   * is the whole reason this has a catch of its own. This list is fetched once,
   * when the menu mounts, so it goes stale without anything telling it: the view
   * can be deleted from another tab, and a colleague can remove the shared one
   * they made. A 404 means the row is already gone, which is precisely the state
   * the operator just asked for - leaving it in the menu would hand them an
   * entry that cannot be removed however many times they confirm.
   *
   * Every other failure KEEPS the row and toasts. Without this catch the
   * rejection travelled out of `ConfirmModal`'s `void run()` as an unhandled
   * promise while `finally` closed the dialog on the way past, so a network blip
   * looked exactly like a successful delete - the dialog shut, the view stayed
   * in the list, and nothing on screen admitted the difference. That is the same
   * silence `remove` in `Milestones.tsx` next door already refuses.
   */
  async function remove(v: SavedView) {
    try {
      await deleteSavedView(v.id);
      setRows((prev) => prev.filter((x) => x.id !== v.id));
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setRows((prev) => prev.filter((x) => x.id !== v.id));
      } else {
        toast(t("common.error"));
      }
    } finally {
      setRemoving(null);
    }
  }

  return (
    <>
      <Pop
        trigger={(open) => (
          <button
            type="button"
            // `crm-fld` is 280px wide by default, which is right for a search
            // field and wrong for a pill in a row of them. Utilities outrank the
            // component layer, so the width comes off here - the same trim
            // `FilterMenu` makes.
            className={cn(
              "crm-fld crm-fld-sel w-auto min-w-0 max-w-[220px] gap-2.5 px-4 text-[12.75px]",
              (open || active) && "is-on",
            )}
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={t("projects.savedViews")}
          >
            <Bookmark className="size-4" />
            {/* The active view's name replaces the label. A control that always
                read "Saved views" would leave the one fact this menu exists to
                carry - which of them is on screen right now - reachable only by
                opening it. */}
            <span className="crm-trunc">{active ? active.name : t("projects.savedViews")}</span>
            <ChevronDown className={cn("size-4 transition-transform", open && "rotate-180")} />
          </button>
        )}
      >
        {loading ? (
          <p className="crm-menu-note">
            <Loader2 className="size-4 animate-spin" />
            {t("common.loading")}
          </p>
        ) : null}

        {/* Not an empty panel. A menu that opens onto nothing reads as broken,
            and a saved view is the one control here whose purpose is not obvious
            from its name - so the empty state is where it gets explained. */}
        {!loading && rows.length === 0 ? (
          <p className="crm-menu-note max-w-[264px]">{t("projects.savedViewsEmpty")}</p>
        ) : null}

        {mine.length > 0 ? <div className="crm-msect">{t("projects.myViews")}</div> : null}
        {mine.map(row)}

        {theirs.length > 0 ? <div className="crm-msect">{t("projects.sharedView")}</div> : null}
        {theirs.map(row)}

        {rows.length > 0 ? <div className="crm-menu-sep" /> : null}
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            setSaving(true);
          }}
        >
          <Plus className="size-4" />
          {t("projects.saveView")}
        </button>
      </Pop>

      {/* Outside the popover: the panel unmounts its children the moment it
          closes, and both dialogs have to outlive the click that opened them. */}
      {saving ? (
        <SaveViewDialog
          query={viewQuery(filters)}
          onClose={() => setSaving(false)}
          onSaved={(v) =>
            // Filtered by id first, because saving the same name over the same
            // filter set answers with the row that already exists rather than a
            // new one - the server's defence against a double-clicked save. Two
            // copies of it in this list would be the duplicate that check exists
            // to prevent, reintroduced on the client.
            setRows((prev) => [...prev.filter((x) => x.id !== v.id), v].sort(bySort))
          }
        />
      ) : null}

      {renaming ? (
        <RenameViewDialog
          view={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={(v) =>
            // Replaced by id and re-sorted, not patched in place: a rename does
            // not move a row (the order is `sortOrder`, not the name), but the
            // list is the one this component holds and swapping the object is
            // what makes the new label appear.
            setRows((prev) => prev.map((x) => (x.id === v.id ? v : x)).sort(bySort))
          }
          // The row is gone rather than un-renameable. Same reason the delete
          // drops it on a 404: this list is fetched once when the menu mounts,
          // so a view removed in another tab is still drawn here, and leaving it
          // in place would offer an edit that can never succeed.
          onGone={(id) => setRows((prev) => prev.filter((x) => x.id !== id))}
        />
      ) : null}

      {removing ? (
        <ConfirmModal
          title={t("projects.deleteView")}
          body={t("projects.deleteViewBody")}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={() => remove(removing)}
          onClose={() => setRemoving(null)}
        />
      ) : null}
    </>
  );
}

/**
 * Naming what is on screen.
 *
 * The filter set is handed in already built rather than read from the register
 * here, so the dialog cannot save a different question from the one that was
 * showing when it opened - the address bar can change underneath a dialog (the
 * back button, a link opened in the same tab) and a save that followed it would
 * store something the operator never saw.
 *
 * Sharing defaults to off, which matches the column's default and its reason: a
 * view that lands in everybody's sidebar the moment it is saved is a view people
 * stop saving. The toggle is present all the same - a private-only feature is
 * one where the team's shared filters live in a chat message.
 */
function SaveViewDialog({
  query,
  onClose,
  onSaved,
}: {
  query: SavedViewQuery;
  onClose: () => void;
  onSaved: (v: SavedView) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      onSaved(await createSavedView({ section: SECTION, name: trimmed, query, shared }));
      toast(t("projects.viewSaved"));
      onClose();
    } catch {
      // Every refusal lands on the same sentence, including the per-section cap
      // at thirty views. That cap is far past "my overdue projects, this
      // client's work, this quarter", and a wording of its own would need a key
      // in both catalogues for a state no operator has reached - so it stays
      // generic until somebody actually meets it.
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t("projects.saveView")}
      label={t("projects.saveView")}
      onClose={onClose}
      className="max-w-[420px]"
    >
      <form onSubmit={submit}>
        <Field label={t("projects.saveViewName")}>
          <label className="crm-inp">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projects.saveViewName")}
              autoComplete="off"
              autoFocus
              required
            />
          </label>
        </Field>

        <label className="crm-fld mt-3 w-auto cursor-pointer gap-2.5 font-semibold text-foreground">
          <input
            type="checkbox"
            checked={shared}
            onChange={(e) => setShared(e.target.checked)}
            className="size-4 flex-none accent-[var(--color-primary)]"
          />
          {t("projects.sharedView")}
        </label>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !name.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Changing a saved view's label, and nothing else about it.
 *
 * The whole dialog is one field on purpose. A PATCH can carry the filter set and
 * the sharing flag as well, and sending them from here would be the bug: the
 * register has moved on since this view was saved, so a dialog that "saved the
 * view" would quietly re-point it at whatever list is on screen now. The body
 * carries `name` alone, so a rename cannot become a re-filter.
 *
 * The stored row is what the field is seeded with and what it is reset to
 * afterwards, not the text that was typed. The server normalises a name -
 * whitespace collapsed, cut at eighty characters - so the label the operator
 * ends up with is not always the one they entered, and a dialog that closed on
 * its own copy would leave the menu disagreeing with the database until the next
 * reload.
 *
 * A name that trims to nothing is refused here rather than sent. The route
 * ignores an empty name and answers 200 with the row unchanged, which is the
 * right thing for an API and the wrong thing to show a person: the dialog would
 * close on a success that renamed nothing.
 */
function RenameViewDialog({
  view,
  onClose,
  onRenamed,
  onGone,
}: {
  view: SavedView;
  onClose: () => void;
  onRenamed: (v: SavedView) => void;
  /** The view no longer exists - deleted from another tab while this was open. */
  onGone: (id: string) => void;
}) {
  const t = useT();
  const toast = useToast();
  const [name, setName] = useState(view.name);
  const [busy, setBusy] = useState(false);

  const trimmed = name.trim();

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !trimmed) return;
    // Nothing to send. The route already answers a no-op with the unchanged row,
    // so this is not about protecting it - it is about not writing an audit
    // entry's worth of round trip for a dialog somebody opened and closed.
    if (trimmed === view.name) {
      onClose();
      return;
    }
    setBusy(true);
    try {
      onRenamed(await updateSavedView(view.id, { name: trimmed }));
      onClose();
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        // 403 lands here too, and it is the same story from the other side: the
        // view stopped being the operator's to edit, which today can only mean
        // the row is not the one this menu thinks it is holding. Dropping it is
        // more honest than leaving a pencil that always fails.
        onGone(view.id);
        onClose();
      } else {
        toast(t("common.error"));
        setBusy(false);
      }
    }
  }

  return (
    <Modal
      title={t("projects.renameView")}
      label={t("projects.renameView")}
      onClose={onClose}
      className="max-w-[420px]"
    >
      <form onSubmit={submit}>
        <Field label={t("projects.saveViewName")}>
          <label className="crm-inp">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("projects.saveViewName")}
              autoComplete="off"
              autoFocus
              required
            />
          </label>
        </Field>

        {/* Said out loud on a shared view only. On a private one it would be a
            reassurance about a risk that does not exist; on a shared one it is
            the fact that separates this from the delete beside it. */}
        {view.shared ? (
          <p className="crm-mut mt-3 text-[12.5px]">{t("projects.renameViewShared")}</p>
        ) : null}

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !trimmed}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("common.save")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
